"use client";

import React from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, X } from "lucide-react";
import type { FormValidationError } from "@/lib/types/forms";

interface FormStatusAlertsProps {
  hasValidationErrors: boolean;
  errorInfo: FormValidationError | null;
  submissionError: string | null;
  onDismissError: () => void;
}

/**
 * Component that displays form status alerts:
 * - Validation errors summary
 * - Submission error
 *
 * Note: Auto-scrolling is handled by the parent EnhancedGenericCreateForm component
 */
export const FormStatusAlerts: React.FC<FormStatusAlertsProps> = ({
  hasValidationErrors,
  errorInfo,
  submissionError,
  onDismissError,
}) => {
  if (!hasValidationErrors && !submissionError) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 bg-card pb-3 -mx-4 px-4 sm:-mx-6 sm:px-6 shadow-sm">
      <div className="space-y-3 pt-3">
        {/* Validation Errors Summary */}
        {hasValidationErrors && errorInfo && (
          <Alert
            variant="destructive"
            className="animate-in slide-in-from-top-2 duration-300 border-2"
          >
            <AlertCircle className="h-5 w-5" />
            <AlertTitle className="text-base font-semibold">
              {errorInfo.count} Error{errorInfo.count > 1 ? "s" : ""} Found - Please Fix Below
            </AlertTitle>
            <AlertDescription>
              <div className="space-y-3 mt-2">
                <p className="text-sm font-medium">Required fields or invalid inputs:</p>
                <ul className="text-sm space-y-2 ml-2">
                  {errorInfo.fields.map(({ fieldLabel, message }, index) => (
                    <li
                      key={fieldLabel}
                      className="flex items-start gap-2 p-2 bg-red-50/50 dark:bg-red-950/40 rounded"
                    >
                      <span className="text-destructive font-bold">{index + 1}.</span>
                      <span>
                        <strong className="text-red-900 dark:text-red-200">{fieldLabel}:</strong>{" "}
                        <span className="text-red-700 dark:text-red-300">{message}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Submission Error */}
        {submissionError && (
          <Alert variant="destructive" className="animate-in slide-in-from-top-2 duration-300">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Submission failed</AlertTitle>
            <AlertDescription>
              <div className="flex items-start justify-between gap-2">
                <span>{submissionError}</span>
                <button
                  type="button"
                  onClick={onDismissError}
                  className="text-muted-foreground hover:text-foreground p-1"
                  aria-label="Dismiss error"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
